import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import AdminLayout from '@/components/admin/AdminLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import { Plus, Pencil, Trash2, Search, Download, Upload } from 'lucide-react';
import * as XLSX from 'xlsx';

interface Hotel { id: string; name: string; address: string | null; zone_id: string | null; airport_id: string | null; is_active: boolean; sort_order: number; }
interface Zone { id: string; name: string; code: string; }
interface Airport { id: string; name: string; code: string; }

const emptyForm = { name: '', address: '', zone_id: '', airport_id: '', is_active: true, sort_order: 0 };

const AdminTransferHotels = () => {
  const { toast } = useToast();
  const [hotels, setHotels] = useState<Hotel[]>([]);
  const [zones, setZones] = useState<Zone[]>([]);
  const [airports, setAirports] = useState<Airport[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Hotel | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [search, setSearch] = useState('');
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    const [h, z, a] = await Promise.all([
      supabase.from('transfer_hotels').select('*').order('sort_order'),
      supabase.from('transfer_zones').select('*'),
      supabase.from('transfer_airports').select('*').order('sort_order'),
    ]);
    setHotels((h.data || []) as Hotel[]);
    setZones((z.data || []) as Zone[]);
    setAirports((a.data || []) as Airport[]);
    setLoading(false);
  };

  const openCreate = () => { setEditing(null); setForm(emptyForm); setDialogOpen(true); };
  const openEdit = (h: Hotel) => {
    setEditing(h);
    setForm({ name: h.name, address: h.address || '', zone_id: h.zone_id || '', airport_id: h.airport_id || '', is_active: h.is_active, sort_order: h.sort_order });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    const payload: any = { name: form.name, address: form.address || null, zone_id: form.zone_id || null, airport_id: form.airport_id || null, is_active: form.is_active, sort_order: form.sort_order };
    try {
      if (editing) {
        const { error } = await supabase.from('transfer_hotels').update(payload).eq('id', editing.id) as any;
        if (error) throw error;
      } else {
        const { error } = await supabase.from('transfer_hotels').insert(payload) as any;
        if (error) throw error;
      }
      toast({ title: editing ? 'Updated' : 'Created' });
      setDialogOpen(false);
      fetchAll();
    } catch (err: any) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  const handleDelete = async (id: string) => {
    const { error } = await supabase.from('transfer_hotels').delete().eq('id', id) as any;
    if (error) toast({ title: 'Error', description: error.message, variant: 'destructive' });
    else { toast({ title: 'Deleted' }); fetchAll(); }
  };

  const downloadTemplate = () => {
    const templateData = [
      {
        name: 'Hotel Example Resort',
        address: '123 Main Street, Punta Cana',
        zone_code: zones.length > 0 ? zones[0].code : 'ZONE_CODE',
        airport_code: airports.length > 0 ? airports[0].code : 'AIRPORT_CODE',
        is_active: 'yes',
        sort_order: 1,
      },
      {
        name: 'Hotel Sample Beach',
        address: '456 Beach Ave, Bavaro',
        zone_code: zones.length > 1 ? zones[1].code : 'ZONE_CODE',
        airport_code: airports.length > 0 ? airports[0].code : 'AIRPORT_CODE',
        is_active: 'yes',
        sort_order: 2,
      },
    ];

    const ws = XLSX.utils.json_to_sheet(templateData);
    ws['!cols'] = [{ wch: 30 }, { wch: 40 }, { wch: 15 }, { wch: 15 }, { wch: 12 }, { wch: 12 }];

    // Add a reference sheet with zones and airports
    const refZones = zones.map(z => ({ code: z.code, name: z.name }));
    const refAirports = airports.map(a => ({ code: a.code, name: a.name }));
    const wsZones = XLSX.utils.json_to_sheet(refZones);
    const wsAirports = XLSX.utils.json_to_sheet(refAirports);

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Hotels');
    XLSX.utils.book_append_sheet(wb, wsZones, 'Zones Reference');
    XLSX.utils.book_append_sheet(wb, wsAirports, 'Airports Reference');
    XLSX.writeFile(wb, 'transfer_hotels_template.xlsx');
    toast({ title: 'Template downloaded' });
  };

  const handleImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);

    try {
      const data = await file.arrayBuffer();
      const wb = XLSX.read(data);
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows: any[] = XLSX.utils.sheet_to_json(ws);

      if (rows.length === 0) {
        toast({ title: 'Error', description: 'The file is empty', variant: 'destructive' });
        return;
      }

      const zoneMap = new Map(zones.map(z => [z.code.toLowerCase(), z.id]));
      const airportMap = new Map(airports.map(a => [a.code.toLowerCase(), a.id]));

      const hotelsToInsert = rows.map((row, i) => {
        const name = String(row.name || '').trim();
        if (!name) throw new Error(`Row ${i + 2}: name is required`);
        return {
          name,
          address: row.address ? String(row.address).trim() : null,
          zone_id: row.zone_code ? (zoneMap.get(String(row.zone_code).toLowerCase()) || null) : null,
          airport_id: row.airport_code ? (airportMap.get(String(row.airport_code).toLowerCase()) || null) : null,
          is_active: row.is_active ? String(row.is_active).toLowerCase() !== 'no' && String(row.is_active) !== '0' && String(row.is_active).toLowerCase() !== 'false' : true,
          sort_order: parseInt(row.sort_order) || 0,
        };
      });

      const { error } = await supabase.from('transfer_hotels').insert(hotelsToInsert) as any;
      if (error) throw error;

      toast({ title: `${hotelsToInsert.length} hotels imported successfully` });
      fetchAll();
    } catch (err: any) {
      toast({ title: 'Import error', description: err.message, variant: 'destructive' });
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const filtered = hotels.filter(h => !search || h.name.toLowerCase().includes(search.toLowerCase()));
  const getZoneName = (zid: string | null) => zones.find(z => z.id === zid)?.name || '-';
  const getAirportCode = (aid: string | null) => airports.find(a => a.id === aid)?.code || '-';

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Transfer Hotels</h1>
            <p className="text-muted-foreground">{hotels.length} hotels registered</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" onClick={downloadTemplate}><Download className="w-4 h-4 mr-2" />Download Template</Button>
            <Button variant="outline" onClick={() => fileInputRef.current?.click()} disabled={importing}>
              <Upload className="w-4 h-4 mr-2" />{importing ? 'Importing...' : 'Import Excel'}
            </Button>
            <input ref={fileInputRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={handleImport} />
            <Button onClick={openCreate}><Plus className="w-4 h-4 mr-2" />Add Hotel</Button>
          </div>
        </div>

        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search hotels..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10" />
        </div>

        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Zone</TableHead>
                  <TableHead>Airport</TableHead>
                  <TableHead>Order</TableHead>
                  <TableHead>Active</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={6} className="text-center py-8">Loading...</TableCell></TableRow>
                ) : filtered.length === 0 ? (
                  <TableRow><TableCell colSpan={6} className="text-center py-8">No hotels found.</TableCell></TableRow>
                ) : filtered.map(h => (
                  <TableRow key={h.id}>
                    <TableCell className="font-medium">{h.name}</TableCell>
                    <TableCell>{getZoneName(h.zone_id)}</TableCell>
                    <TableCell>{getAirportCode(h.airport_id)}</TableCell>
                    <TableCell>{h.sort_order}</TableCell>
                    <TableCell>{h.is_active ? '✅' : '❌'}</TableCell>
                    <TableCell className="text-right space-x-2">
                      <Button variant="outline" size="sm" onClick={() => openEdit(h)}><Pencil className="w-4 h-4" /></Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild><Button variant="destructive" size="sm"><Trash2 className="w-4 h-4" /></Button></AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader><AlertDialogTitle>Delete {h.name}?</AlertDialogTitle><AlertDialogDescription>This cannot be undone.</AlertDialogDescription></AlertDialogHeader>
                          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => handleDelete(h.id)}>Delete</AlertDialogAction></AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent>
            <DialogHeader><DialogTitle>{editing ? 'Edit Hotel' : 'Add Hotel'}</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div><Label>Name</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} /></div>
              <div><Label>Address</Label><Input value={form.address} onChange={e => setForm({...form, address: e.target.value})} /></div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Zone</Label>
                  <Select value={form.zone_id} onValueChange={v => setForm({...form, zone_id: v})}>
                    <SelectTrigger><SelectValue placeholder="Select zone" /></SelectTrigger>
                    <SelectContent>{zones.map(z => <SelectItem key={z.id} value={z.id}>{z.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Airport</Label>
                  <Select value={form.airport_id} onValueChange={v => setForm({...form, airport_id: v})}>
                    <SelectTrigger><SelectValue placeholder="Select airport" /></SelectTrigger>
                    <SelectContent>{airports.map(a => <SelectItem key={a.id} value={a.id}>{a.code} - {a.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Sort Order</Label><Input type="number" value={form.sort_order} onChange={e => setForm({...form, sort_order: parseInt(e.target.value) || 0})} /></div>
                <div className="flex items-center gap-2 pt-6"><Switch checked={form.is_active} onCheckedChange={v => setForm({...form, is_active: v})} /><Label>Active</Label></div>
              </div>
              <Button onClick={handleSave} className="w-full">{editing ? 'Update' : 'Create'}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminTransferHotels;
